import { useState } from 'react';
import { motion } from 'framer-motion';
import { Download, FileText, Loader2 } from 'lucide-react';
import { jsPDF } from 'jspdf';

const CompanyProfilePDF = ({ variant = 'hero', className = '' }) => {
  const [isGenerating, setIsGenerating] = useState(false);

  // Company data
  const companyData = {
    name: 'GLOW SPACE LLC',
    tagline: 'Aluminium & Glass Solutions',
    phone: '+971 56 683 2840',
    email: 'info@glow-space.com',
    website: 'www.glow-space.com',
    locations: [
      { type: 'Head Office', address: 'Champion Tower, Dubai Sports City, Dubai, UAE' },
      { type: 'Showroom', address: 'Al Quoz, Dubai, UAE' },
      { type: 'Al Ain Office', address: 'Asharej, Al Ain, UAE' },
      { type: 'Workshop', address: 'Maliha, Sharjah, UAE' },
    ],
    logoUrl: 'https://customer-assets.emergentagent.com/job_build-preview-73/artifacts/hqbb1m0k_WhatsApp%20Image%202026-03-29%20at%2019.35.30.jpeg',
  };

  // Get backend URL for image proxy
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Services list with images
  const services = [
    { name: 'Aluminium Windows & Doors', image: 'https://images.unsplash.com/photo-1616843221271-a3447ca74df3?w=400&h=300&fit=crop' },
    { name: 'Glass Partitions', image: 'https://images.unsplash.com/photo-1770048532712-4fde5ef7eb90?w=400&h=300&fit=crop' },
    { name: 'Bi-Folding & Sliding Doors', image: 'https://images.unsplash.com/photo-1758998202918-d921125a700f?w=400&h=300&fit=crop' },
    { name: 'Shower Enclosures', image: 'https://images.unsplash.com/photo-1771929662486-f793e08f0f16?w=400&h=300&fit=crop' },
    { name: 'Glass Railings & Balustrades', image: 'https://images.pexels.com/photos/35699361/pexels-photo-35699361.jpeg?w=400&h=300&fit=crop' },
    { name: 'Curtain Walls & Facades', image: 'https://images.pexels.com/photos/27985107/pexels-photo-27985107.jpeg?w=400&h=300&fit=crop' },
    { name: 'Skylights', image: 'https://images.pexels.com/photos/11844536/pexels-photo-11844536.jpeg?w=400&h=300&fit=crop' },
    { name: 'Mirrors & Decorative Glass', image: 'https://images.pexels.com/photos/8134818/pexels-photo-8134818.jpeg?w=400&h=300&fit=crop' },
    { name: 'ACP Cladding', image: 'https://images.pexels.com/photos/10958536/pexels-photo-10958536.jpeg?w=400&h=300&fit=crop' },
  ];

  // Helper function to load image as base64
  const loadImageAsBase64 = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        try {
          const dataURL = canvas.toDataURL('image/jpeg', 0.8);
          resolve(dataURL);
        } catch (e) {
          reject(e);
        }
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = url;
    });
  };

  // Load image via fetch to handle CORS better
  const loadImageViaFetch = async (url) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (e) {
      console.error('Failed to fetch image:', e);
      return null;
    }
  };

  const generatePDF = async () => {
    setIsGenerating(true);
    
    try {
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - (margin * 2);
      
      // Sky blue color scheme
      const skyBlue = [14, 116, 144];
      const darkBlue = [12, 74, 110];
      const lightBlue = [186, 230, 253];
      const green = [37, 211, 102];

      // Try to load company logo via proxy
      let logoBase64 = null;
      try {
        // Use proxy endpoint to avoid CORS
        const proxyUrl = `${BACKEND_URL}/api/image-proxy?url=${encodeURIComponent(companyData.logoUrl)}`;
        logoBase64 = await loadImageViaFetch(proxyUrl);
        if (!logoBase64) {
          // Try direct fetch as fallback
          logoBase64 = await loadImageViaFetch(companyData.logoUrl);
        }
      } catch (e) {
        console.log('Could not load logo, using text fallback:', e);
      }

      // ============ PAGE 1 - COVER ============
      
      // Full page background
      doc.setFillColor(...darkBlue);
      doc.rect(0, 0, pageWidth, pageHeight, 'F');
      
      // Decorative circles
      doc.setFillColor(skyBlue[0], skyBlue[1], skyBlue[2]);
      doc.setGState(new doc.GState({ opacity: 0.1 }));
      doc.circle(pageWidth - 30, 50, 80, 'F');
      doc.circle(-20, pageHeight - 60, 100, 'F');
      doc.setGState(new doc.GState({ opacity: 1 }));

      // Company Logo
      const logoY = 50;
      if (logoBase64) {
        // Add actual logo - larger size to show full logo with text
        doc.addImage(logoBase64, 'JPEG', pageWidth / 2 - 35, logoY - 30, 70, 70);
      } else {
        // Fallback: white circle with initials
        doc.setFillColor(255, 255, 255);
        doc.circle(pageWidth / 2, logoY, 25, 'F');
        doc.setFontSize(24);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...skyBlue);
        doc.text('GS', pageWidth / 2, logoY + 3, { align: 'center' });
      }

      // Company Name
      doc.setFontSize(32);
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.text(companyData.name, pageWidth / 2, logoY + 60, { align: 'center' });
      
      // Tagline
      doc.setFontSize(14);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...lightBlue);
      doc.text(companyData.tagline, pageWidth / 2, logoY + 72, { align: 'center' });

      // Divider line
      doc.setDrawColor(...green);
      doc.setLineWidth(1);
      doc.line(pageWidth / 2 - 40, logoY + 85, pageWidth / 2 + 40, logoY + 85);

      // Company Profile Title
      doc.setFontSize(24);
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.text('COMPANY PROFILE', pageWidth / 2, logoY + 105, { align: 'center' });

      // Year badge
      doc.setFillColor(...green);
      doc.roundedRect(pageWidth / 2 - 30, logoY + 115, 60, 22, 3, 3, 'F');
      doc.setFontSize(12);
      doc.setTextColor(255, 255, 255);
      doc.text('Established in UAE', pageWidth / 2, logoY + 129, { align: 'center' });

      // Contact info at bottom
      const contactY = pageHeight - 45;
      doc.setFontSize(11);
      doc.setTextColor(...lightBlue);
      doc.text(companyData.phone + '  |  ' + companyData.email, pageWidth / 2, contactY, { align: 'center' });
      doc.setFontSize(10);
      doc.text('Dubai  |  Al Ain  |  Sharjah', pageWidth / 2, contactY + 8, { align: 'center' });

      // ============ PAGE 2 - ABOUT US ============
      doc.addPage();
      
      // Header bar
      doc.setFillColor(...darkBlue);
      doc.rect(0, 0, pageWidth, 35, 'F');
      doc.setFontSize(20);
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.text('About Us', pageWidth / 2, 23, { align: 'center' });

      let yPos = 50;

      // About section
      doc.setFontSize(13);
      doc.setTextColor(50, 50, 50);
      doc.setFont('helvetica', 'bold');
      doc.text('Who We Are', margin, yPos);
      
      yPos += 10;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      const aboutText = `Glow Space LLC is an original Canadian company operating in the UAE, bringing together the best of North American engineering standards with deep local expertise. We specialize in premium aluminium and glass solutions for both residential and commercial sectors.

Our commitment to quality, combined with competitive pricing, has established us as a trusted partner for property owners, real estate companies, and contractors across Dubai and the Emirates.

We pride ourselves on delivering exceptional craftsmanship, meeting deadlines, and providing outstanding customer service that exceeds expectations.`;
      
      const splitAbout = doc.splitTextToSize(aboutText, contentWidth);
      doc.text(splitAbout, margin, yPos);
      
      yPos += splitAbout.length * 5.5 + 20;

      // Why Choose Us
      doc.setFontSize(13);
      doc.setTextColor(50, 50, 50);
      doc.setFont('helvetica', 'bold');
      doc.text('Why Choose Us', margin, yPos);
      
      yPos += 10;
      
      const whyChoose = [
        'Canadian Quality Standards - North American engineering excellence',
        'Local UAE Expertise - Deep understanding of regional requirements',
        'Competitive Pricing - Premium quality at affordable rates',
        'Professional Team - Dedicated staff with years of experience',
        'Timely Delivery - Projects completed on schedule',
        'After-Sales Support - Ongoing maintenance and service',
      ];

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      whyChoose.forEach((item, index) => {
        doc.setFillColor(...green);
        doc.circle(margin + 3, yPos + 1, 2, 'F');
        doc.setTextColor(50, 50, 50);
        doc.text(item, margin + 10, yPos + 3);
        yPos += 10;
      });

      yPos += 15;

      // Stats boxes
      doc.setFillColor(245, 250, 255);
      doc.roundedRect(margin, yPos, contentWidth, 40, 5, 5, 'F');
      
      const statY = yPos + 25;
      const statWidth = contentWidth / 3;
      
      // Stat 1
      doc.setFontSize(28);
      doc.setTextColor(...skyBlue);
      doc.setFont('helvetica', 'bold');
      doc.text('150+', margin + statWidth / 2, statY - 5, { align: 'center' });
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.setFont('helvetica', 'normal');
      doc.text('Projects Completed', margin + statWidth / 2, statY + 8, { align: 'center' });
      
      // Stat 2
      doc.setFontSize(28);
      doc.setTextColor(...skyBlue);
      doc.setFont('helvetica', 'bold');
      doc.text('50+', margin + statWidth + statWidth / 2, statY - 5, { align: 'center' });
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.setFont('helvetica', 'normal');
      doc.text('Happy Clients', margin + statWidth + statWidth / 2, statY + 8, { align: 'center' });
      
      // Stat 3
      doc.setFontSize(28);
      doc.setTextColor(...skyBlue);
      doc.setFont('helvetica', 'bold');
      doc.text('5+', margin + statWidth * 2 + statWidth / 2, statY - 5, { align: 'center' });
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.setFont('helvetica', 'normal');
      doc.text('Years Experience', margin + statWidth * 2 + statWidth / 2, statY + 8, { align: 'center' });

      // ============ PAGE 3 - SERVICES ============
      doc.addPage();
      
      // Header bar
      doc.setFillColor(...darkBlue);
      doc.rect(0, 0, pageWidth, 35, 'F');
      doc.setFontSize(20);
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.text('Our Services', pageWidth / 2, 23, { align: 'center' });

      yPos = 45;

      // Load service images and create grid
      const serviceImagePromises = services.slice(0, 6).map(async (service, index) => {
        try {
          const imgData = await loadImageAsBase64(service.image);
          return { ...service, imgData, index };
        } catch (e) {
          return { ...service, imgData: null, index };
        }
      });

      const loadedServices = await Promise.all(serviceImagePromises);

      // Services grid (2 columns, 3 rows for first 6)
      const cardWidth = (contentWidth - 10) / 2;
      const cardHeight = 55;
      
      loadedServices.forEach((service, idx) => {
        const col = idx % 2;
        const row = Math.floor(idx / 2);
        const x = margin + col * (cardWidth + 10);
        const y = yPos + row * (cardHeight + 8);

        // Card background
        doc.setFillColor(245, 250, 255);
        doc.roundedRect(x, y, cardWidth, cardHeight, 3, 3, 'F');
        
        // Image placeholder or actual image
        if (service.imgData) {
          try {
            doc.addImage(service.imgData, 'JPEG', x + 3, y + 3, cardWidth - 6, 32);
          } catch (e) {
            // Draw placeholder
            doc.setFillColor(220, 230, 240);
            doc.rect(x + 3, y + 3, cardWidth - 6, 32, 'F');
          }
        } else {
          doc.setFillColor(220, 230, 240);
          doc.rect(x + 3, y + 3, cardWidth - 6, 32, 'F');
        }
        
        // Number badge
        doc.setFillColor(...green);
        doc.circle(x + 10, y + 10, 6, 'F');
        doc.setFontSize(9);
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.text(String(idx + 1), x + 10, y + 12, { align: 'center' });
        
        // Service name
        doc.setFontSize(10);
        doc.setTextColor(50, 50, 50);
        doc.setFont('helvetica', 'bold');
        const serviceName = doc.splitTextToSize(service.name, cardWidth - 10);
        doc.text(serviceName, x + 5, y + 42);
      });

      yPos += 3 * (cardHeight + 8) + 10;

      // Remaining services list
      doc.setFontSize(11);
      doc.setTextColor(50, 50, 50);
      doc.setFont('helvetica', 'bold');
      doc.text('Additional Services:', margin, yPos);
      
      yPos += 8;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      services.slice(6).forEach((service, index) => {
        doc.setFillColor(...green);
        doc.circle(margin + 3, yPos + 1, 2, 'F');
        doc.text(`${service.name}`, margin + 10, yPos + 3);
        yPos += 8;
      });

      // ============ PAGE 4 - CONTACT ============
      doc.addPage();
      
      // Header bar
      doc.setFillColor(...darkBlue);
      doc.rect(0, 0, pageWidth, 35, 'F');
      doc.setFontSize(20);
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.text('Contact Us', pageWidth / 2, 23, { align: 'center' });

      yPos = 50;

      // Contact card
      doc.setFillColor(245, 250, 255);
      doc.roundedRect(margin, yPos, contentWidth, 50, 5, 5, 'F');

      const contactStartY = yPos + 15;
      const leftX = margin + 15;
      
      // Phone
      doc.setFillColor(...green);
      doc.circle(leftX, contactStartY, 5, 'F');
      doc.setFontSize(11);
      doc.setTextColor(50, 50, 50);
      doc.setFont('helvetica', 'bold');
      doc.text('Phone', leftX + 12, contactStartY + 1);
      doc.setFont('helvetica', 'normal');
      doc.text(companyData.phone, leftX + 12, contactStartY + 8);

      // Email
      doc.setFillColor(...green);
      doc.circle(leftX, contactStartY + 22, 5, 'F');
      doc.setFont('helvetica', 'bold');
      doc.text('Email', leftX + 12, contactStartY + 23);
      doc.setFont('helvetica', 'normal');
      doc.text(companyData.email, leftX + 12, contactStartY + 30);

      yPos += 60;

      // Locations Section
      doc.setFontSize(13);
      doc.setTextColor(...skyBlue);
      doc.setFont('helvetica', 'bold');
      doc.text('Our Locations', margin, yPos);
      
      yPos += 10;

      // Location cards
      companyData.locations.forEach((loc, index) => {
        doc.setFillColor(245, 250, 255);
        doc.roundedRect(margin, yPos, contentWidth, 20, 3, 3, 'F');
        
        doc.setFillColor(index === 0 ? green[0] : skyBlue[0], index === 0 ? green[1] : skyBlue[1], index === 0 ? green[2] : skyBlue[2]);
        doc.circle(margin + 12, yPos + 10, 5, 'F');
        
        doc.setFontSize(11);
        doc.setTextColor(50, 50, 50);
        doc.setFont('helvetica', 'bold');
        doc.text(loc.type, margin + 22, yPos + 8);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.text(loc.address, margin + 22, yPos + 15);
        
        yPos += 24;
      });

      yPos += 10;

      // WhatsApp CTA
      doc.setFillColor(...green);
      doc.roundedRect(margin, yPos, contentWidth, 28, 5, 5, 'F');
      doc.setFontSize(13);
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.text('Chat with us on WhatsApp', pageWidth / 2, yPos + 12, { align: 'center' });
      doc.setFontSize(11);
      doc.text(companyData.phone, pageWidth / 2, yPos + 22, { align: 'center' });

      yPos += 40;

      // Working hours
      doc.setFontSize(12);
      doc.setTextColor(50, 50, 50);
      doc.setFont('helvetica', 'bold');
      doc.text('Working Hours', pageWidth / 2, yPos, { align: 'center' });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.text('Sunday - Thursday: 8:00 AM - 6:00 PM', pageWidth / 2, yPos + 10, { align: 'center' });

      // Footer
      doc.setFillColor(...darkBlue);
      doc.rect(0, pageHeight - 20, pageWidth, 20, 'F');
      doc.setFontSize(10);
      doc.setTextColor(255, 255, 255);
      doc.text('Thank you for considering Glow Space LLC for your project', pageWidth / 2, pageHeight - 8, { align: 'center' });

      // Save the PDF
      doc.save('Glow_Space_Company_Profile.pdf');
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('There was an error generating the PDF. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Hero variant - prominent button
  if (variant === 'hero') {
    return (
      <motion.button
        onClick={generatePDF}
        disabled={isGenerating}
        className={`inline-flex items-center gap-2 px-6 py-3.5 bg-sky-800/90 backdrop-blur-sm border border-sky-600/50 text-white rounded-lg hover:bg-sky-700 transition-all duration-300 font-medium shadow-lg ${className}`}
        data-testid="download-profile-btn"
        whileHover={{ scale: 1.05, boxShadow: '0 10px 40px rgba(14, 116, 144, 0.4)' }}
        whileTap={{ scale: 0.98 }}
      >
        {isGenerating ? (
          <>
            <Loader2 size={20} className="animate-spin" />
            <span>Generating...</span>
          </>
        ) : (
          <>
            <Download size={20} />
            <span>Company Profile</span>
          </>
        )}
      </motion.button>
    );
  }

  // Compact variant for footer or other locations
  return (
    <motion.button
      onClick={generatePDF}
      disabled={isGenerating}
      className={`inline-flex items-center gap-2 text-sky-300 hover:text-white transition-colors ${className}`}
      data-testid="download-profile-link"
      whileHover={{ x: 5 }}
    >
      {isGenerating ? (
        <>
          <Loader2 size={16} className="animate-spin" />
          <span>Generating...</span>
        </>
      ) : (
        <>
          <FileText size={16} />
          <span>Download Company Profile</span>
        </>
      )}
    </motion.button>
  );
};

export default CompanyProfilePDF;
